import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { recordAdImpression, recordAdClick } from '@/services/api/ads'
import { useAdStore } from '@/store/useAdStore'
import type { Ad, AdPlacement } from '@/types/ad'

interface AdCarouselProps {
  placement?: AdPlacement
  className?: string
}

export const AdCarousel = memo(function AdCarousel({ placement, className = '' }: AdCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const impressionsRef = useRef<Set<string>>(new Set())

  const { isLoading, lastFetched, fetchAds, getAdsByPlacement } = useAdStore()
  const ads = useMemo(() => getAdsByPlacement(placement), [getAdsByPlacement, placement, lastFetched])
  const isLoaded = !isLoading && lastFetched !== null

  // Fetch ads via store (uses cache if fresh)
  useEffect(() => {
    fetchAds()
  }, [fetchAds])

  // Record impression for the current ad (once per ad per page load)
  useEffect(() => {
    if (ads.length === 0) return

    const ad = ads[currentIndex]
    if (ad && !impressionsRef.current.has(ad.id)) {
      impressionsRef.current.add(ad.id)
      recordAdImpression(ad.id).catch(() => {})
    }
  }, [ads, currentIndex])

  // Auto-rotate every 5 seconds
  const startAutoRotate = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (ads.length <= 1) return

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % ads.length)
    }, 5000)
  }, [ads.length])

  useEffect(() => {
    startAutoRotate()
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [startAutoRotate])

  const goTo = useCallback((index: number) => {
    setCurrentIndex(index)
    startAutoRotate()
  }, [startAutoRotate])

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + ads.length) % ads.length)
    startAutoRotate()
  }, [ads.length, startAutoRotate])

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % ads.length)
    startAutoRotate()
  }, [ads.length, startAutoRotate])

  const handleClick = useCallback((ad: Ad) => {
    recordAdClick(ad.id).catch(() => {})
    if (ad.linkUrl) {
      window.open(ad.linkUrl, '_blank', 'noopener,noreferrer')
    }
  }, [])

  // Don't render anything if no ads or still loading
  if (!isLoaded || ads.length === 0) return null

  return (
    <div className={`relative group rounded-xl shadow-md overflow-hidden bg-white ${className}`}>
      {/* Slides */}
      <div className="relative w-full" style={{ aspectRatio: '2.7 / 1' }}>
        <div
          className="flex h-full transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {ads.map((ad) => (
            <div
              key={ad.id}
              className="w-full h-full flex-shrink-0 cursor-pointer"
              onClick={() => handleClick(ad)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleClick(ad)
                }
              }}
              aria-label={`Sponsored: ${ad.title} by ${ad.sponsor}`}
            >
              {failedImages.has(ad.id) ? (
                <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400">
                  <span className="text-sm">Ad unavailable</span>
                </div>
              ) : (
                <img
                  src={ad.imageUrl}
                  alt={ad.title}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  onError={() => setFailedImages((prev) => new Set(prev).add(ad.id))}
                />
              )}
            </div>
          ))}
        </div>

        {/* Sponsored label */}
        <span className="absolute bottom-2 right-2 text-xs text-white/80 bg-black/40 px-2 py-0.5 rounded">
          Sponsored
        </span>
      </div>

      {/* Prev / Next arrows (visible on hover) */}
      {ads.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goPrev() }}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Previous ad"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goNext() }}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Next ad"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {ads.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {ads.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); goTo(index) }}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentIndex
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
              aria-label={`Go to ad ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
})
